<template>
  <div class="unserved-wrapper">
    <McTable
      ref="mcTable"
      v-bind="tableProps"
      :apiClient="apiClient"
      :fields="fields"
      :hasCheckbox="allVehiclesHaveFleetTask"
      :hasSelectAll="false"
      :hasDelete="false"
      :customHandlers="customHandlers"
      stickyHeader
      hasEmptyState
      @handleSelectValue="handleSelectValue"
    >
      <template #emptyState>
        <b-card>
          <div class="empty-table-container">
            <img src="@/assets/images/success-icon.png" alt="Success Icon" />
            <div class="content-wrapper">
              <strong>{{ $t('routeOptimization.noData.title') }}</strong>
              <p>{{ $t('routeOptimization.noData.subtitle') }}</p>
            </div>
          </div>
        </b-card>
      </template>
      <template #cell(number)="{ value, item }">
        <div class="do-number-wrapper">
          <router-link
            class="text-secondary-route-link"
            :to="{ name: 'Delivery Detail', params: { id: item?.id } }"
          >
            {{ value || '-' }}
          </router-link>
          <i
            v-if="isInvalidDoToCreateNewRo(item)"
            v-b-tooltip.hover.bottomright="{
              title: $t('routeOptimization.tooltip.DOhasAssignment'),
              customClass: 'custom-tooltip-wrapper',
            }"
            class="mc-icon icon-fill_alert-triangle"
          />
          <i
            v-if="isInvalidDoUgroupToCreateNewRo(item)"
            v-b-tooltip.hover.bottomright="{
              title: $t('routeOptimization.tooltip.DOhasDifferentUserGroup'),
              customClass: 'custom-tooltip-wrapper',
            }"
            class="mc-icon icon-fill_alert-triangle"
          />
        </div>
      </template>
      <template #[`cell(status.name)`]="{ value, item }">
        <span
          v-if="item.status?.color"
          class="__status-dots"
          :style="{ backgroundColor: item.status.color }"
        />
        <span class="mc-text-status">{{ value }}</span>
      </template>
      <template #cell(created_on)="{ value }">
        <template v-if="value">
          <div>{{ formatIndexDate(value).date }}</div>
          <div class="mc-datetime">
            {{ formatIndexDate(value).time }}
          </div>
        </template>
        <template v-else> - </template>
      </template>
      <template #cell(product_categories)="{ value }">
        <McChipList
          v-if="value?.length"
          :items="value"
          :max-visible="3"
          itemTextField="name"
          showAllMode="dropdown"
          :showAllDropdownTitle="$t('setting.list.product.field.boxType')"
          showAllDropdownTrigger="action"
        />
      </template>
      <template #[`cell(unserved_reason.raw_type)`]="{ value }">
        <div class="red-text">
          {{ value }}
        </div>
      </template>
    </McTable>
  </div>
</template>

<script>
import McTable from '@/components/McTable'
import McChipList from '@/components/McChipList'

import * as RO from '@/services/rest-api/lib/route-optimization'
import { formatIndexDate } from '@/helpers/tzDate'
import { Helper } from '@/mixin/helper'

import unservedFields from '../../../lib/fields/unserved-delivery-list-fields.js'
import { EventBus } from '../../../lib/event-bus.js'

import { mapState } from 'vuex'

export default {
  name: 'UnservedContent',
  components: {
    McTable,
    McChipList,
  },
  mixins: [Helper],
  props: {
    vehicles: {
      type: Array,
      default: () => {},
    },
    isPending: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['selectedItemsChange'],
  data() {
    return {
      isLoading: true,
      selectedItems: [],
      unservedItems: [],
    }
  },
  computed: {
    ...mapState('routeOptimization', ['showResponse', 'unservedShowResponse']),
    id() {
      return this.$route.params.id
    },
    fields() {
      return unservedFields(this)
    },
    allVehiclesHaveFleetTask() {
      return (
        (this.vehicles?.every((vehicle) => vehicle.fleet_task_id !== null) &&
          this.unservedShowResponse?.metadata?.total_served > 0) ||
        false
      )
    },
    apiClient() {
      return {
        get: async (filter) => {
          await this.waitForShowResponse()

          const updatedFilter = {
            ...filter,
            filters: filter.filters
              ? [...filter.filters, 'ro.vehicle_id is null']
              : ['ro.vehicle_id is null'],
          }

          const res = await RO.getSettingDelivery(this.id)(updatedFilter)
          this.$store.dispatch('routeOptimization/setUnservedShowResponse', res)
          this.unservedItems = res.data.paginated_result

          if (this.isPending && res.metadata.total_unserved > 0) {
            this.showDeliveryWarning()
          }
          EventBus.$emit('set-pending-status', false)

          return res
        },
      }
    },
    customHandlers() {
      return {
        isItemSelectDisabled: (item) => {
          return this.isInvalidDoToCreateNewRo(item) || this.isInvalidDoUgroupToCreateNewRo(item)
        },
      }
    },
    isInvalidDoToCreateNewRo() {
      return (item) =>
        (item.errors?.length > 0 &&
          item.errors[0] ===
            'FLEET_TASK.ROUTE_OPTIMIZATION_ACTIVITY_DELIVERY_ORDER_INVALID_STATUS') ||
        item.status.name === 'Dibatalkan'
    },
    isInvalidDoUgroupToCreateNewRo() {
      return (item) =>
        item.errors?.length > 0 &&
        item.errors[0] === 'FLEET_TASK.ROUTE_OPTIMIZATION.DELIVERY_ORDER_USER_GROUP_ID.DIFFERENT'
    },
    tableProps() {
      return {
        limitOptions: [5, 10, 25, 50, 100],
        initialLimit: 5,
      }
    },
  },
  watch: {
    showResponse: {
      handler(newValue, _) {
        if (newValue?.status?.raw_type !== 'SUCCESS') return

        this.isLoading = false
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    formatIndexDate,

    handleUnselectAll() {
      this.selectedItems = []
      this.$emit('selectedItemsChange', [])
    },

    handleSelectValue(selectedIds) {
      const selectedItemIds = Object.keys(selectedIds).filter((id) => selectedIds[id])

      this.selectedItems = selectedItemIds
      this.$emit('selectedItemsChange', selectedItemIds)
    },

    getFirstUnservedItem() {
      return this.unservedItems.length > 0 ? this.unservedItems[0] : null
    },

    waitForShowResponse() {
      return new Promise((resolve) => {
        if (!this.isLoading) {
          resolve()
          return
        }

        const unwatch = this.$watch('isLoading', () => {
          unwatch()
          resolve()
        })
      })
    },

    showDeliveryWarning() {
      this.$bvModal.show('ro-delivery-warning-modal')
    },
  },
}
</script>
<style>
.custom-tooltip-wrapper .tooltip-inner {
  max-width: 300px;
  word-wrap: break-word;
}
</style>
